import { useEffect, useId, useState, type RefObject } from 'react';
import { CheckCircle2 } from 'lucide-react';
// Import runtime constants from the source module, NOT the @ucp/web/lib barrel:
// the barrel's `export * as db` pulls node-only `pg` into the browser bundle.
import {
  ACCESS_REQUEST_REASON_MAX,
  REQUESTABLE_ROLES,
  type RequestableRole,
  type RequestableSchool,
} from '../../lib/types/accessRequest';
import { Sheet } from '../ui/Sheet';
import { Field } from '../ui/Field';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Button } from '../ui/Button';
import { useRequestAccess } from '../../hooks/useRequestAccess';

/**
 * Self-service access-request dialog (PRO-0580 / ADR-036, AC-1/AC-2/AC-11). A
 * focus-trapped centred `Sheet` (ADR-015 elevated-dialog glass scope) opened from
 * either the zero-role `AccessNotConfiguredView` gate (school picked from the TD-9
 * `GET /access-requests/schools` endpoint) or a contextual 403 affordance (role
 * pre-filled from the capability reverse-map, school fixed to the current tenant).
 * Submits via `useRequestAccess`; on success shows the `AR-…` reference. The role
 * choice is bounded to `REQUESTABLE_ROLES` (the client mirror of the server EoP
 * guard, TD-3) so `L6`/`CEO`/`COO`/safeguarding roles can never be offered.
 */

interface RequestAccessDialogProps {
  open: boolean;
  onClose: () => void;
  returnFocusRef?: RefObject<HTMLElement | null>;
  /** 403-contextual path: pre-select (and lock) the role derived from the reverse-map. */
  prefilledRole?: RequestableRole;
  /** 403-contextual path: the current tenant school (no picker needed). */
  fixedSchool?: RequestableSchool;
}

const ROLE_LABELS: Record<RequestableRole, string> = {
  L1_SENDER: 'Send messages (Sender)',
  L2_APPROVER: 'Approve broadcasts (Approver)',
  COMPLAINT_HANDLER: 'Handle complaints',
  ATR_REQUESTER: 'Request Authority to Recruit',
  OPS_SITE_CONTRIBUTOR: 'Record operational issues (Weekly Report)',
  OPS_ACTION_OWNER: 'Own operational actions (Weekly Report)',
};

export function RequestAccessDialog({
  open,
  onClose,
  returnFocusRef,
  prefilledRole,
  fixedSchool,
}: RequestAccessDialogProps) {
  const titleId = useId();
  const { state, loadSchools, submit, reset } = useRequestAccess();

  const [role, setRole] = useState<RequestableRole>(prefilledRole ?? REQUESTABLE_ROLES[0]);
  const [schoolId, setSchoolId] = useState<string>(fixedSchool?.schoolId ?? '');
  const [reason, setReason] = useState('');
  const [schools, setSchools] = useState<readonly RequestableSchool[]>(
    fixedSchool ? [fixedSchool] : [],
  );
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  // Reset + (for the zero-role path) load the school picker each time it opens.
  useEffect(() => {
    if (!open) {
      return;
    }
    setRole(prefilledRole ?? REQUESTABLE_ROLES[0]);
    setReason('');
    setShowValidation(false);
    reset();
    if (fixedSchool) {
      setSchools([fixedSchool]);
      setSchoolId(fixedSchool.schoolId);
      return;
    }
    setSchoolId('');
    setSchoolsLoading(true);
    let cancelled = false;
    void loadSchools().then((list) => {
      if (!cancelled) {
        setSchools(list);
        setSchoolsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, prefilledRole, fixedSchool, loadSchools, reset]);

  const submitting = state.status === 'submitting';
  const success = state.status === 'success' ? state : null;
  const schoolError =
    showValidation && !schoolId ? 'Please choose the school you need access to.' : undefined;

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!schoolId) {
      setShowValidation(true);
      return;
    }
    void submit({ requestedRole: role, schoolId, reason });
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      {...(returnFocusRef ? { returnFocusRef } : {})}
      anchor="center"
      id="request-access-dialog"
      aria-labelledby={titleId}
    >
      <div className="flex max-h-[85vh] w-full flex-col">
        <div className="border-b border-border px-5 py-4">
          <h2 id={titleId} className="text-lg font-semibold text-text-primary">
            Request access
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            Choose what you need. A platform administrator will review your request.
          </p>
        </div>

        {success ? (
          <div className="flex flex-1 flex-col items-center gap-3 overflow-y-auto px-5 py-8 text-center">
            <CheckCircle2 aria-hidden className="h-10 w-10 text-success" />
            <p className="text-base font-semibold text-text-primary">
              {success.outcome === 'auto_closed'
                ? 'You already have this access.'
                : 'Thanks — your request was sent.'}
            </p>
            <p className="text-sm text-text-secondary">
              Reference{' '}
              <span className="font-mono font-medium text-text-primary">{success.reference}</span>.
              {success.outcome === 'auto_closed'
                ? ' No action is needed.'
                : ' An administrator has been notified.'}
            </p>
            <Button variant="primary" onClick={onClose} className="mt-2">
              Done
            </Button>
          </div>
        ) : (
          <form
            className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4"
            onSubmit={onSubmit}
            noValidate
          >
            <Select
              id="request-role"
              label="What access do you need?"
              value={role}
              onChange={(e) => setRole(e.target.value as RequestableRole)}
              disabled={submitting || prefilledRole !== undefined}
              required
            >
              {REQUESTABLE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </Select>

            {fixedSchool ? (
              <Field htmlFor="request-school-fixed" label="School">
                <input
                  id="request-school-fixed"
                  type="text"
                  value={fixedSchool.name}
                  readOnly
                  className="w-full rounded-md border border-border bg-surface-muted px-3 py-2 text-text-secondary"
                />
              </Field>
            ) : (
              <Select
                id="request-school"
                label="Which school?"
                value={schoolId}
                onChange={(e) => setSchoolId(e.target.value)}
                disabled={submitting || schoolsLoading}
                required
                error={schoolError}
              >
                <option value="" disabled>
                  {schoolsLoading ? 'Loading schools…' : 'Select a school'}
                </option>
                {schools.map((s) => (
                  <option key={s.schoolId} value={s.schoolId}>
                    {s.name}
                  </option>
                ))}
              </Select>
            )}

            <Field
              htmlFor="request-reason"
              label="Why do you need this? (optional)"
              description="A short note helps the administrator approve your request faster."
            >
              <Textarea
                id="request-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={ACCESS_REQUEST_REASON_MAX}
                disabled={submitting}
                aria-describedby="request-reason-description"
              />
            </Field>

            {state.status === 'error' ? (
              <p role="alert" className="text-sm text-danger">
                {state.message}
              </p>
            ) : null}

            <div className="mt-1 flex items-center justify-end gap-2">
              <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send request'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Sheet>
  );
}
